import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";

import { marketingApi } from "../client.js";
import { CardRails, ErrorBanner } from "../shared.js";
import { HowItWorksCard } from "./agent-status/HowItWorksCard.js";
import { PlatformsCard } from "./agent-status/PlatformsCard.js";
import { ScheduleCard } from "./agent-status/ScheduleCard.js";
import { SpendCard } from "./agent-status/SpendCard.js";
import { StateHero } from "./agent-status/StateHero.js";
import { deriveState } from "./agent-status/statusHelpers.js";
import { ThinkingFeed } from "./agent-status/ThinkingFeed.js";

/**
 * Agent Status — the cockpit. One screen that answers the three operator
 * questions the agent never used to surface in one place:
 *   • What is it doing right now? (running / paused / sleeping + mode)
 *   • What is it spending, and when does it sleep? (budget + reset)
 *   • What is it thinking, and what runs next? (live trace + per-loop schedule)
 *
 * This file is the data + composition shell. The cockpit cards live under
 * `agent-status/` (StateHero / SpendCard / ScheduleCard / ThinkingFeed /
 * PlatformsCard / HowItWorksCard), the shared pills in `atoms.tsx`, and the
 * pure derived-state + relative-time helpers in `statusHelpers.ts` (re-exported
 * below so existing importers + the test keep their path).
 */

// Re-exported so `StatusPage.test.tsx` (and any future importer) keeps the
// `./StatusPage.js` path for the pure helpers.
export type { AgentState } from "./agent-status/statusHelpers.js";
export { agoFmt, autonomyLabel, deriveState, untilFmt } from "./agent-status/statusHelpers.js";

export function StatusPage(): JSX.Element {
  const settings = useQuery({
    queryKey: ["marketing", "agent-settings"],
    queryFn: marketingApi.getAgentSettings,
    refetchInterval: 30_000,
  });
  const econ = useQuery({
    queryKey: ["marketing", "economics"],
    queryFn: marketingApi.getEconomics,
    refetchInterval: 30_000,
    retry: 0,
  });
  const status = useQuery({
    queryKey: ["marketing", "agent-status"],
    queryFn: marketingApi.agentStatus,
    refetchInterval: 15_000,
    retry: 0,
  });
  const thoughts = useQuery({
    queryKey: ["marketing", "thoughts", "status-feed"],
    queryFn: () => marketingApi.listThoughts({ since_minutes: 720, limit: 25 }),
    refetchInterval: 5_000,
    retry: 0,
  });
  const platforms = useQuery({
    queryKey: ["marketing", "platforms"],
    queryFn: marketingApi.listPlatformAccounts,
    refetchInterval: 60_000,
    retry: 0,
  });

  // The agent-settings read is the page's spine (it drives the hero state +
  // every other card's framing). If it can't load, the marketing app is
  // unreachable — show the shared diagnostic banner rather than a half page.
  if (settings.isError) return <ErrorBanner error={settings.error} />;

  const state = deriveState(settings.data, econ.data);

  return (
    <Box sx={{ display: "grid", gap: 2 }} data-testid="marketing-status">
      <StateHero settings={settings.data} econ={econ.data} />
      {/* Two balanced rails: the spend KPI sits above the tall per-loop
          schedule on the left; the live trace + platform readiness stack on
          the right. Neither rail forces the other's row height, so the short
          spend card no longer punches a gap down the middle of the page. */}
      <CardRails
        left={
          <>
            <SpendCard econ={econ.data} />
            <ScheduleCard rows={status.data} state={state} />
          </>
        }
        right={
          <>
            <ThinkingFeed thoughts={thoughts.data} />
            <PlatformsCard accounts={platforms.data} />
          </>
        }
      />
      <HowItWorksCard />
    </Box>
  );
}
